
// Enhanced hook for real-time token balance management
import { useState, useEffect, useCallback } from 'react';
import { tokenService } from '../services/tokenService';
import { useAuth } from '../context/AuthContext';

export const useTokenBalance = () => {
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useAuth();

  // Stable refresh function with no external dependencies
  const refreshBalance = useCallback(async () => {
    if (!user?.id) {
      setBalance(0);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    try {
      setError(null);
      const newBalance = await tokenService.getUserTokenBalance();
      setBalance(newBalance);
      setIsInitialized(true);
    } catch (err) {
      console.error('❌ useTokenBalance: Error refreshing balance:', err);
      setError(err.message);
      setIsInitialized(true);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies to prevent loops

  // Initial load effect - stable and only runs on user change
  useEffect(() => {
    let isActive = true;

    if (!user?.id) {
      setBalance(0);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    const loadInitialBalance = async () => {
      try {
        setLoading(true);
        setError(null);
        const newBalance = await tokenService.getUserTokenBalance();
        
        if (isActive) {
          setBalance(newBalance);
          setIsInitialized(true);
        }
      } catch (err) {
        if (isActive) {
          console.error('❌ useTokenBalance: Error loading initial balance:', err);
          setError(err.message);
          setIsInitialized(true);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadInitialBalance();
    
    return () => {
      isActive = false;
    };
  }, [user?.id]); // Only depend on user ID

  // Real-time subscription - stable and only runs when user changes
  useEffect(() => {
    if (!user?.id || !isInitialized) return;

    const unsubscribe = tokenService.subscribeToTokenBalance((newBalance) => {
      setBalance(newBalance);
      setError(null);
    });

    return unsubscribe;
  }, [user?.id, isInitialized]); // Only depend on user ID and initialization

  // Storage events for cross-tab updates - stable
  useEffect(() => {
    if (!user?.id) return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'token_purchase_completed') {
        tokenService.getUserTokenBalance().then(newBalance => {
          setBalance(newBalance);
          setError(null);
        }).catch(err => {
          console.error('❌ useTokenBalance: Error updating from storage:', err);
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user?.id]); // Only depend on user ID

  return {
    balance,
    loading,
    error,
    refreshBalance,
    isInitialized
  };
};
